/**
 * Mantenimiento: instalaciones y edificio (luz, plomería, aire, mobiliario,
 * limpieza, cerraduras). Lo de cómputo sigue por Tickets.
 *
 * Las llaves (categoría, urgencia, estatus) son las mismas que valida
 * MantenimientoBuss en el servidor: si se agrega una, va en los dos lados.
 */

export type EstatusMantenimiento =
  | "SOLICITADA"
  | "EN_ATENCION"
  | "EN_ESPERA"
  | "RESUELTA"
  | "CERRADA"
  | "CANCELADA";

export interface Mantenimiento {
  idSolicitud?: number;
  folio?: string;
  estatus?: EstatusMantenimiento;
  categoria?: string;
  urgencia?: string;
  ubicacion?: string;
  descripcion?: string;
  telefonoContacto?: string | null;
  idUsuarioSolicita?: number;
  solicitante?: string;
  idUsuarioResponsable?: number | null;
  responsable?: string | null;
  fechaAsignacion?: string | null;
  fechaSolicitud?: string;
  fechaUltimaAfectacion?: string;
  fechaResuelta?: string | null;
  fechaCierre?: string | null;
  cierreAutomatico?: boolean;
  fechaCancelacion?: string | null;
  idUsuarioCancela?: number | null;
  reaperturas?: number;
  fotos?: number;
}

export interface MantenimientoHistorial {
  idHistorial?: number;
  idSolicitud?: number;
  accion?: string;
  estatusAnterior?: string | null;
  estatusNuevo?: string | null;
  comentario?: string | null;
  idUsuario?: number | null;
  usuario?: string | null;
  fecha?: string;
}

export interface MantenimientoFoto {
  idFoto?: number;
  idSolicitud?: number;
  nombreArchivo?: string;
  extension?: string;
  tamanio?: number;
  /** SOLICITUD (la subió quien pidió) | ATENCION (la subió quien atiende). */
  etapa?: string;
  idUsuario?: number;
  usuario?: string;
  fecha?: string;
}

export interface MantenimientoReq {
  solicitud?: Mantenimiento;
  idSolicitud?: number;
  idFoto?: number;
  estatus?: string;
  comentario?: string | null;
  idUsuarioResponsable?: number | null;
  busca?: string;
  filtroEstatus?: string;
  soloMias?: boolean;
  pagina?: number;
  porPagina?: number;
}

export interface MantenimientoRes {
  solicitudes?: Mantenimiento[];
  solicitud?: Mantenimiento;
  historial?: MantenimientoHistorial[];
  fotos?: MantenimientoFoto[];
  foto?: MantenimientoFoto;
  totalItems?: number;
  conteos?: Record<string, number>;
  puedeAtender?: boolean;
  responsables?: Mantenimiento[];
  yo?: string;
  idYo?: number;
  diasCierre?: number;
  idGenerado?: number;
  folioGenerado?: string;
  codigoError?: string | null;
  mensajeError?: string | null;
}

/** Una opción con su texto, su ayuda y su icono. */
export interface OpcionMantenimiento {
  valor: string;
  texto: string;
  ayuda: string;
  icono: string;
}

export const CATEGORIAS_MANTENIMIENTO: OpcionMantenimiento[] = [
  { valor: "ELECTRICO", texto: "Eléctrico", ayuda: "Luz, contactos, apagadores", icono: "electrical_services" },
  { valor: "PLOMERIA", texto: "Plomería", ayuda: "Fugas, baños, lavabos", icono: "plumbing" },
  { valor: "CLIMA", texto: "Aire acondicionado", ayuda: "No enfría, gotea, hace ruido", icono: "ac_unit" },
  { valor: "MOBILIARIO", texto: "Mobiliario", ayuda: "Sillas, escritorios, puertas", icono: "chair" },
  { valor: "LIMPIEZA", texto: "Limpieza", ayuda: "Derrames, basura, sanitarios", icono: "cleaning_services" },
  { valor: "CERRADURAS", texto: "Cerraduras y accesos", ayuda: "Chapas, llaves, controles", icono: "lock" },
  { valor: "OTRO", texto: "Otro", ayuda: "Algo más del edificio", icono: "handyman" },
];

export const URGENCIAS_MANTENIMIENTO: OpcionMantenimiento[] = [
  { valor: "BAJA", texto: "Puede esperar", ayuda: "No afecta el trabajo", icono: "schedule" },
  { valor: "MEDIA", texto: "Esta semana", ayuda: "Molesta, pero se puede trabajar", icono: "event" },
  { valor: "ALTA", texto: "Urgente", ayuda: "Impide trabajar o es un riesgo", icono: "priority_high" },
];

/** Etiqueta, clase y icono de cada estatus: iguales en las tres pantallas. */
export const ESTATUS_MANTENIMIENTO: Record<string, { texto: string; clase: string; icono: string }> = {
  SOLICITADA: { texto: "Solicitada", clase: "es-nueva", icono: "fiber_new" },
  EN_ATENCION: { texto: "En atención", clase: "es-atencion", icono: "engineering" },
  EN_ESPERA: { texto: "En espera", clase: "es-espera", icono: "hourglass_top" },
  RESUELTA: { texto: "Resuelta", clase: "es-resuelta", icono: "task_alt" },
  CERRADA: { texto: "Cerrada", clase: "es-cerrada", icono: "check_circle" },
  CANCELADA: { texto: "Cancelada", clase: "es-cancelada", icono: "block" },
};

export function categoriaDe(valor?: string | null): OpcionMantenimiento | undefined {
  return CATEGORIAS_MANTENIMIENTO.find((c) => c.valor === valor);
}

/**
 * Las pedidas desde el 15/10/2026 no traen tipo (el formulario ya no lo
 * pide): se pintan con el icono de mantenimiento y sin nombre de tipo.
 */
export const SIN_TIPO_MANTENIMIENTO: OpcionMantenimiento = { valor: "", texto: "", ayuda: "", icono: "handyman" };

/** El tipo de una solicitud, o SIN_TIPO_MANTENIMIENTO si no trae o no se conoce. */
export function tipoMantenimiento(valor?: string | null): OpcionMantenimiento {
  return categoriaDe(valor) ?? SIN_TIPO_MANTENIMIENTO;
}

export function urgenciaDe(valor?: string | null): OpcionMantenimiento | undefined {
  return URGENCIAS_MANTENIMIENTO.find((u) => u.valor === valor);
}

export function estatusDe(valor?: string | null): { texto: string; clase: string; icono: string } {
  return ESTATUS_MANTENIMIENTO[valor || ""] || { texto: valor || "—", clase: "es-cerrada", icono: "help_outline" };
}

/** Las fotos que acepta el servidor (FotoMantenimiento). */
export const FOTOS_ACEPTADAS = ".jpg,.jpeg,.png,.webp";
export const FOTO_MAX_MB = 10;
export const FOTOS_MAX_POR_ETAPA = 6;
